import {
    fetchSupervisors,
    prepareBatchId,
    fetchEmployees,
    exportPaintPayroll,
    exportDrywallPayroll,
    fetchBatchReports,
    fetchPrintTimecards
} from "../dataLayer/v1BatchExport";
import { Supervisor, EmployeeTimeSheet } from "../interfaces/V1Interface";

interface BatchExportOptions {
    exportType: 'paint' | 'drywall';
    supervisorId: string;
    startDate: string;
    endDate: string;
    employeeIDs?: string[];
  }  

class V1BatchExportManager {
    // Method to get supervisors for a specific week
    async getSupervisors(timeSheetDate: string, sdate: string, edate: string): Promise<Supervisor[]> {
        try {
            const supervisors = await fetchSupervisors(timeSheetDate, sdate, edate);
            console.log("Supervisors retrieved successfully");
            return supervisors;
        } catch (error) {
            console.error("Error getting supervisors:", error);
            throw error;
        }
    }

    // Method to prepare a new batch ID
    async createBatchId(batchPrefix: string): Promise<string> {
        try {
            const batchId = await prepareBatchId(batchPrefix);
            console.log("Batch ID prepared successfully:", batchId);
            return batchId;
        } catch (error) {
            console.error("Error preparing batch ID:", error);
            throw error;
        }
    }

    // Method to get employees by supervisor
    async getEmployees(
        supervisorId: string,
        sdate: string,
        edate: string,
        timeSheetDate: string = "",
        search: string = "",
        conditionId: string = "0",
        pageNumber: number = 0
    ): Promise<EmployeeTimeSheet[]> {
        try {
            const employees = await fetchEmployees(
                supervisorId,
                sdate,
                edate,
                timeSheetDate,
                search,
                conditionId,
                pageNumber
            );
            console.log("Employees retrieved successfully:", employees);
            return employees as EmployeeTimeSheet[];
        } catch (error) {
            console.error("Error getting employees:", error);
            throw error;
        }
    }

    // Method to export payroll data
    async exportPayroll(batchId: string, options: BatchExportOptions): Promise<any> {
        try {
          const { supervisorId, startDate, endDate, employeeIDs, exportType } = options;
      
          let result;
          if (exportType === 'paint') {
            result = await exportPaintPayroll(batchId, supervisorId, startDate, endDate, employeeIDs);
          } else {
            result = await exportDrywallPayroll(batchId, supervisorId, startDate, endDate, employeeIDs);
          }
      
          console.log(`${exportType} payroll exported successfully`);
          return result;
        } catch (error) {
          console.error(`Error exporting ${options.exportType} payroll:`, error);
          throw error;
        }
      }      

    // Method to get batch reports
    async getReports(batchId: string): Promise<{regularReport: any, ddReport: any}> {
        try {
            const reports = await fetchBatchReports(batchId);
            console.log("Batch reports retrieved successfully");
            return reports;
        } catch (error) {
            console.error("Error getting batch reports:", error);
            throw error;
        }
    }

    // Method to print timecards for a supervisor
    async printTimecards(supervisorId: string, startDate: string, endDate: string): Promise<string> {
        try {
            const pdfPath = await fetchPrintTimecards(supervisorId, startDate, endDate);
            console.log("Timecards printed successfully");
            return pdfPath;
        } catch (error) {
            console.error("Error printing timecards:", error);
            throw error;
        }
    }

    // Comprehensive method to handle full export process
    async completeBatchExport(
        batchId: string,
        exportType: 'paint' | 'drywall',
        supervisorId: string,
        startDate: string,
        endDate: string,
        selectedEmployeeIds?: string[]
    ): Promise<{ exportResult: any; reports: any }> {
        try {
        // Step 1: Export payroll data
        const exportResult = await this.exportPayroll(batchId, {
            exportType,
            supervisorId,
            startDate,
            endDate,
            employeeIDs: selectedEmployeeIds,
        });
    
        // Step 2: Get reports
        const reports = await this.getReports(batchId);
    
        console.log("Batch export completed successfully");
        return {
            exportResult,
            reports,
        };
        } catch (error) {
        console.error("Error during complete batch export:", error);
        throw error;
        }
    }
  
}

// Exporting an instance of V1BatchExportManager to use throughout the application
export default new V1BatchExportManager();